import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { OctagonXIcon } from "lucide-react";
import { api } from "@/client/lib";
import { useAuth } from "@/client/lib/auth-context";
import { Alert, AlertDescription, AlertTitle } from "@/client/components/base/alert";
import { Badge } from "@/client/components/base/badge";
import { Button } from "@/client/components/base/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/client/components/base/card";
import { Spinner } from "@/client/components/base/spinner";

const MePage = () => {
    const navigate = useNavigate();
    const { token, logout } = useAuth();
    const [username, setUsername] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!token) {
            navigate("/login", { replace: true });
            return;
        }

        (async () => {
            const { data, error } = await api.users.me.get({
                headers: { authorization: `Bearer ${token}` },
            });

            setIsLoading(false);

            if (error) {
                if (error.status === 401) {
                    logout();
                    return;
                }
                setError(`Failed to load profile (${error.status})`);
                return;
            }

            setUsername(data?.username ?? null);
        })();
    }, [token, navigate, logout]);

    const handleLogout = () => {
        logout();
    };

    return (
        <main className="relative flex min-h-svh items-center justify-center overflow-hidden bg-background p-4 sm:p-8">
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 [background:radial-gradient(70%_60%_at_50%_-10%,var(--primary)_0%,transparent_60%)] opacity-10"
            />
            <Card className="relative w-full max-w-md">
                <CardHeader>
                    <CardTitle>Your account</CardTitle>
                    <CardDescription>Authenticated via JWT.</CardDescription>
                </CardHeader>

                <CardContent>
                    {isLoading ? (
                        <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
                            <Spinner />
                            Loading profile...
                        </div>
                    ) : error ? (
                        <Alert variant="destructive">
                            <OctagonXIcon aria-hidden="true" />
                            <AlertTitle>Something went wrong</AlertTitle>
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    ) : (
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <p className="text-sm text-muted-foreground">Logged in as</p>
                                <p className="text-lg font-medium">{username ?? "Unknown"}</p>
                            </div>
                            <Badge variant="secondary">Authenticated</Badge>
                        </div>
                    )}
                </CardContent>

                <CardFooter className="justify-center">
                    <Button variant="outline" onClick={handleLogout}>
                        Log out
                    </Button>
                </CardFooter>
            </Card>
        </main>
    );
};

export default MePage;
